import { useEffect, useMemo, useState } from "react"
import { Modal } from "flowbite-react"
import Loading from "../../../../common/components/ui/loading/Loading"
import { IOperationalManagement } from "../../../../types/operational-management"
import { VEHICLE_STATUS } from "@/types/vehicle.type"

type Props = {
  operationalManagement: IOperationalManagement
  onClose: () => void
}

// eslint-disable-next-line react-refresh/only-export-components
export const STATUS_LABELS = {
  [VEHICLE_STATUS.AVAILABLE]: "Disponível",
  [VEHICLE_STATUS.MISSING_DATA]: "Dados incompletos",
  [VEHICLE_STATUS.RENTED]: "Locado",
  [VEHICLE_STATUS.INACTIVE]: "Inativo",
  [VEHICLE_STATUS.WAITING_PAYMENT]: "Aguardando pagamento",
  [VEHICLE_STATUS.SELLED]: "Veículo vendido",
  [VEHICLE_STATUS.UNDER_PREPARATION]: "Em preparação",
  [VEHICLE_STATUS.UNDER_REVIEW]: "Revisão Programada",
  [VEHICLE_STATUS.IN_REPAIR]: "Em manutenção",
  [VEHICLE_STATUS.IN_REVISION]: "Reparo estético",
  [VEHICLE_STATUS.INSURANCE]: "Abertura de acionamento",
  [VEHICLE_STATUS.TRIGGER_OPENING]: "Proteção acionada",
  [VEHICLE_STATUS.IMPOUNDED]: "Apreendido",
  [VEHICLE_STATUS.THIRD_PARTY_INSURANCE]: "Seguro de Terceiros",
  [VEHICLE_STATUS.PREPARATION_FOR_SALE]: "Motos para Venda",
  [VEHICLE_STATUS.IN_TRANSIT]: "Em trânsito",
  [VEHICLE_STATUS.THEFT_ROBBERY]: "Furto/Roubo",
  [VEHICLE_STATUS.MISAPPROPRIATION]: "Apropriação indébita",
  [VEHICLE_STATUS.VEHICLE_ON_RESERVED]: "Moto reserva",
  [VEHICLE_STATUS.TRIAGE]: "Triagem",
};

// eslint-disable-next-line react-refresh/only-export-components
export function getStatusLabelsVehicle(status: VEHICLE_STATUS) {
  return STATUS_LABELS[status] || status;
}

export default function ModalViewNotesOperationalManagement({ operationalManagement: initialData, onClose }: Props) {
  const [operationalManagement, setOperationalManagement] = useState<IOperationalManagement | null>(null)

  const handleCloseModal = () => onClose()

  useEffect(() => {
    setOperationalManagement(initialData)
  }, [initialData])

  const sortedNotes = useMemo(() => {
    const notes = operationalManagement?.notes ?? []
    return [...notes].sort((a, b) => {
      const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0
      const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0
      return bTime - aTime
    })
  }, [operationalManagement?.notes])

  const currentNote = useMemo(() => {
    if (!operationalManagement) return null
    return sortedNotes.find(n => n.status === operationalManagement.status) ?? null
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedNotes, operationalManagement?.status])

  const previousNotes = useMemo(() => {
    if (!sortedNotes.length) return []
    return sortedNotes.filter(n => n._id !== currentNote?._id)
  }, [sortedNotes, currentNote?._id])

  const formatDateTime = (iso?: string) =>
    iso
      ? new Date(iso).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
      : 'Data não disponível'

  const formatDate = (iso?: string) =>
    iso
      ? new Date(iso.replace(/Z$/, "")).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
      : 'Data não informada'

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const NoteItem = ({ note }: { note: any }) => (
    <li key={note._id ?? `${note.createdAt}`} className="border-b border-gray-200 p-2">
      <span className="text-gray-500 text-md">{formatDateTime(note.createdAt)}</span>&nbsp;-&nbsp;
      <span className="text-gray-600 text-md">{note.author}</span>:
      <br />
      <span className="text-gray-800 text-md font-semibold">{note.content}</span>
      <br />
      <span className="text-gray-700 text-sm">Data prevista de resolução: </span>
      <span className="text-gray-800 text-md font-semibold">{formatDate(note.expectedResolutionDate)}</span>
      {note.status ? (
        <>
          <br />
          <span className="text-gray-700 text-sm">Status da observação: </span>
          <span className="text-gray-800 text-sm">{getStatusLabelsVehicle(note.status)}</span>
        </>
      ) : null}
    </li>
  )

  return (
    <Modal size='5xl' className='relative' show={true} onClose={handleCloseModal}>
      <Modal.Header className='text-black'>
        <div className="flex items-center justify-start gap-3 text-black">
          <span>{`Observações - ${operationalManagement?.licensePlate || 'Carregando...'}`}</span>
        </div>
      </Modal.Header>

      <Modal.Body
        className='w-full relative border-gray flex flex-col gap-6 text-black'
        style={{ minHeight: 'calc(50vh - 300px)', height: 'calc(100vh - 300px)' }}
      >
        {operationalManagement ? (
          <div className="flex flex-col gap-6">
            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Observação atual</h3>
              {currentNote ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <ul><NoteItem note={currentNote} /></ul>
                </div>
              ) : (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-gray-600">
                  Nenhuma observação atual encontrada para o status: <b>{getStatusLabelsVehicle(operationalManagement.status) || '—'}</b>
                </div>
              )}
            </section>

            <section className="flex-1 overflow-auto">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Observações anteriores</h3>
              {previousNotes.length ? (
                <ul>
                  {previousNotes.map(note => <NoteItem key={note._id ?? `${note.createdAt}`} note={note} />)}
                </ul>
              ) : (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-gray-600">
                  Não há observações anteriores.
                </div>
              )}
            </section>
          </div>
        ) : (
          <Loading />
        )}
      </Modal.Body>
    </Modal>
  )
}
